import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { LocalVideoTrack, RemoteVideoTrack } from 'agora-rtc-react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiMaximize,
  FiMic,
  FiMicOff,
  FiMinimize2,
  FiMonitor,
  FiPhoneOff,
  FiRefreshCw,
  FiVideo,
  FiVideoOff,
} from 'react-icons/fi';
import { getImage } from '../../utils';
import { useCallSession } from './callSessionContext';
import { useCallMedia } from './callMediaEngine';
import { CallStatsChip } from './CallStatsChip';
import { getCallTiming, subscribeCallTiming } from './callTimingStore';

const formatCountdown = (secs) => {
  const total = Math.max(0, Number(secs) || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${s}`;
  return `${m}:${s}`;
};

const useCallTiming = () => useSyncExternalStore(subscribeCallTiming, getCallTiming, getCallTiming);

const ControlBtn = ({ onClick, title, variant = 'default', disabled = false, active = false, children }) => (
  <button
    type="button"
    disabled={disabled}
    className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors text-white disabled:opacity-40 disabled:cursor-not-allowed ${
      variant === 'danger'
        ? 'bg-rose-600 hover:bg-rose-500'
        : active
          ? 'bg-sky-600 hover:bg-sky-500'
          : 'bg-slate-800 hover:bg-slate-700'
    }`}
    onClick={onClick}
    title={title}
  >
    {children}
  </button>
);

const IdentityCard = ({ name, avatar, compact = false, statusText = '' }) => (
  <div className="h-full w-full bg-slate-900 flex items-center justify-center">
    <div className="flex flex-col items-center text-center px-4">
      <img
        src={getImage(avatar, 'AVATAR')}
        className={`${compact ? 'w-14 h-14' : 'w-24 h-24'} rounded-full object-cover border-2 border-white/25`}
        alt={name}
      />
      <p className={`text-white mt-3 ${compact ? 'text-xs' : 'text-base'} font-medium`}>{name}</p>
      {statusText ? <p className="text-white/60 text-xs mt-1">{statusText}</p> : null}
    </div>
  </div>
);

const isScreenLikeUser = (user) => {
  const track = user?.videoTrack;
  if (!track) return false;
  try {
    const msTrack = track.getMediaStreamTrack?.();
    const settings = msTrack?.getSettings?.() || {};
    const w = settings.width || 0;
    const h = settings.height || 0;
    if (w > 0 && h > 0) return w / h > 1.5;
    return Boolean(settings.displaySurface);
  } catch (_) {
    return false;
  }
};

/**
 * Presentational full-call UI — media owned by CallMediaEngine.
 */
const CallStage = ({ localName, localAvatar, chromeVisible, onUserActivity }) => {
  const navigate = useNavigate();
  const { endCall, minimizeCall, appointmentPath } = useCallSession();
  const media = useCallMedia();
  const [mainView, setMainView] = useState('remote');

  const {
    micOn,
    camOn,
    isScreenSharing,
    localVideoTrack,
    remoteUser,
    remotePresent,
    micAvailable,
    camAvailable,
    screenShareSupported,
    connectionState,
    error,
    audioBlocked,
    setMicOn,
    setCamOn,
    startScreenShare,
    stopScreenShare,
    enableBlockedAudio,
    retryJoin,
  } = media || {};

  const timing = useCallTiming();

  useEffect(() => {
    if (!remotePresent) setMainView('local');
  }, [remotePresent]);

  const leavePath = appointmentPath || '/courses';
  const inWaitLobby = !timing.sessionStarted;
  const showRemoteAsMain = Boolean(remoteUser) && remotePresent && mainView === 'remote' && !inWaitLobby;
  const canSwap = Boolean(remoteUser) && remotePresent && !inWaitLobby;
  const remoteHasVideo = Boolean(remoteUser?.hasVideo && remoteUser?.videoTrack);
  const remoteScreenLike = isScreenLikeUser(remoteUser);

  const goBack = () => {
    navigate(leavePath);
  };

  const handleMinimize = async () => {
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch (_) {}
    }
    minimizeCall();
    goBack();
  };

  const handleEndCall = async () => {
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch (_) {}
    }
    await endCall();
    goBack();
  };

  const renderRemote = (style = {}) => {
    if (!remoteUser) return null;
    if (!remoteHasVideo) {
      return (
        <div className="h-full w-full bg-slate-900 flex items-center justify-center">
          <p className="text-white/60 text-sm">Camera off</p>
        </div>
      );
    }
    // RemoteVideoTrack renders video only — it never touches the remote audio
    // track (RemoteUser with playAudio={false} would stop() engine-played audio).
    return (
      <RemoteVideoTrack
        track={remoteUser.videoTrack}
        play
        style={{
          width: '100%',
          height: '100%',
          objectFit: remoteScreenLike ? 'contain' : 'cover',
          ...style,
        }}
      />
    );
  };

  const renderLocal = ({ compact = false, slotKey = 'local-main' } = {}) => {
    if (isScreenSharing && localVideoTrack) {
      return (
        <div key={slotKey} className="relative h-full w-full">
          <LocalVideoTrack
            track={localVideoTrack}
            play
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>
      );
    }

    // Keep the local video element mounted across cam toggles to avoid play()/load races.
    if (!localVideoTrack) {
      return (
        <IdentityCard
          name={localName}
          avatar={localAvatar}
          compact={compact}
          statusText={!camAvailable ? 'Camera unavailable' : ''}
        />
      );
    }

    // LocalVideoTrack only plays/stops the video element — the media engine
    // remains the single owner of track.setEnabled() for mic and camera.
    return (
      <div key={slotKey} className="relative h-full w-full">
        <LocalVideoTrack
          track={localVideoTrack}
          play={camOn}
          style={{ width: '100%', height: '100%' }}
        />
        {!camOn ? (
          <div className="absolute inset-0 z-10">
            <IdentityCard
              name={localName}
              avatar={localAvatar}
              compact={compact}
              statusText={!camAvailable ? 'Camera unavailable' : ''}
            />
          </div>
        ) : null}
      </div>
    );
  };

  const renderMain = () => {
    if (inWaitLobby) {
      return (
        <IdentityCard
          name={localName}
          avatar={localAvatar}
          statusText="Waiting for course…"
        />
      );
    }
    if (showRemoteAsMain) return renderRemote();
    return renderLocal({ slotKey: 'local-main' });
  };

  const renderPip = () => {
    if (inWaitLobby) {
      if (!remotePresent) return null;
      return (
        <div className="h-full w-full bg-slate-900 flex items-center justify-center px-3">
          <p className="text-white/70 text-xs text-center">Joined — waiting…</p>
        </div>
      );
    }
    if (!canSwap) return null;
    if (showRemoteAsMain) return renderLocal({ compact: true, slotKey: 'local-pip' });
    return renderRemote();
  };

  return (
    <div
      className="absolute inset-0 bg-slate-950"
      onMouseMove={onUserActivity}
      onTouchStart={onUserActivity}
    >
      <div className="h-full w-full">{renderMain()}</div>

      {(canSwap || (inWaitLobby && remotePresent)) && (
        <button
          type="button"
          className="absolute top-20 right-4 w-[220px] h-[140px] rounded-2xl overflow-hidden border-2 border-white/20 shadow-2xl bg-slate-900"
          onClick={() => {
            if (!inWaitLobby) setMainView((p) => (p === 'remote' ? 'local' : 'remote'));
          }}
          disabled={inWaitLobby}
        >
          {renderPip()}
          {!inWaitLobby ? (
            <span className="absolute top-2 right-2 z-20 bg-slate-950/70 text-white p-1.5 rounded-full">
              <FiRefreshCw size={13} />
            </span>
          ) : null}
        </button>
      )}

      {inWaitLobby && (
        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none px-4">
          <div className="bg-slate-950/85 border border-white/15 rounded-2xl px-6 py-5 text-center backdrop-blur-md shadow-2xl max-w-sm">
            <p className="text-white/70 text-sm mb-1">Course starts in</p>
            <p className="text-white text-3xl font-semibold tabular-nums tracking-wide">
              {formatCountdown(timing.startInSeconds)}
            </p>
            <p className="text-white/70 text-sm mt-3">
              {remotePresent
                ? "You're both here — the lesson starts soon."
                : 'Waiting for the other participant…'}
            </p>
          </div>
        </div>
      )}

      {timing.sessionStarted && !remotePresent && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
          <div className="bg-slate-900/80 border border-white/10 rounded-2xl px-6 py-4 text-center backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse inline-block mr-2" />
            <span className="text-white/80 text-sm font-medium">Waiting for other participant…</span>
          </div>
        </div>
      )}

      {connectionState === 'RECONNECTING' && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-40 bg-amber-500/90 text-white text-sm px-4 py-2 rounded-full shadow-lg">
          Reconnecting…
        </div>
      )}

      {error && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-slate-950/70 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm text-center shadow-xl">
            <p className="text-nt-blue font-semibold mb-2">Connection issue</p>
            <p className="text-nt-grey text-sm mb-4">{error}</p>
            <div className="flex justify-center gap-2">
              <button
                type="button"
                className="bg-nt-blue text-white rounded-lg px-4 py-2 text-sm"
                onClick={() => retryJoin?.()}
              >
                Retry
              </button>
              <button
                type="button"
                className="bg-slate-100 text-nt-blue rounded-lg px-4 py-2 text-sm"
                onClick={handleEndCall}
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}

      {audioBlocked && timing.sessionStarted && (
        <button
          type="button"
          onClick={() => enableBlockedAudio?.()}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 bg-white text-nt-blue font-medium px-5 py-3 rounded-xl shadow-xl"
        >
          Click to enable audio
        </button>
      )}

      {isScreenSharing && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-sky-600 text-white text-sm px-4 py-2 rounded-full shadow-lg">
          <FiMonitor size={14} />
          <span>You are presenting</span>
          <button
            type="button"
            className="ml-1 underline font-medium"
            onClick={() => stopScreenShare?.()}
          >
            Stop
          </button>
        </div>
      )}

      <div
        className={`absolute bottom-5 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-slate-950/85 border border-white/20 rounded-full px-3 py-2.5 backdrop-blur-md shadow-2xl transition-opacity duration-300 ${
          chromeVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <ControlBtn
          onClick={() => setCamOn?.((v) => !v)}
          title={camOn ? 'Turn off camera' : 'Turn on camera'}
          disabled={!camAvailable || isScreenSharing}
        >
          {camOn ? <FiVideo size={18} /> : <FiVideoOff size={18} />}
        </ControlBtn>

        <ControlBtn
          onClick={() => setMicOn?.((v) => !v)}
          title={micOn ? 'Mute' : 'Unmute'}
          disabled={!micAvailable}
        >
          {micOn ? <FiMic size={18} /> : <FiMicOff size={18} />}
        </ControlBtn>

        {screenShareSupported && (
          <ControlBtn
            onClick={() => startScreenShare?.()}
            title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
            active={isScreenSharing}
          >
            <FiMonitor size={18} />
          </ControlBtn>
        )}

        {canSwap && (
          <ControlBtn onClick={() => setMainView((p) => (p === 'remote' ? 'local' : 'remote'))} title="Swap view">
            <FiRefreshCw size={18} />
          </ControlBtn>
        )}

        <ControlBtn onClick={handleMinimize} title="Minimize — keep call running">
          <FiMinimize2 size={18} />
        </ControlBtn>

        <ControlBtn onClick={handleEndCall} title="End call" variant="danger">
          <FiPhoneOff size={18} />
        </ControlBtn>
      </div>
    </div>
  );
};

const VideoChat = () => {
  const navigate = useNavigate();
  const { currentProfile } = useSelector((state) => state.profile);
  const { callSession, minimizeCall, appointmentPath, registerNavigateAway } = useCallSession();
  const media = useCallMedia();
  const timing = useCallTiming();
  const stageRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cssFullscreen, setCssFullscreen] = useState(false);
  const [chromeVisible, setChromeVisible] = useState(true);
  const hideTimerRef = useRef(null);

  const isCallActive = Boolean(
    callSession?.channel && callSession?.token && Number.isInteger(callSession?.uid),
  );

  const closeInSeconds = timing.closeInSeconds;

  useEffect(() => {
    registerNavigateAway?.(() => {
      navigate(appointmentPath || '/courses');
    });
  }, [registerNavigateAway, navigate, appointmentPath]);

  const bumpChrome = useCallback(() => {
    setChromeVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (isFullscreen || cssFullscreen) {
      hideTimerRef.current = setTimeout(() => setChromeVisible(false), 3000);
    }
  }, [isFullscreen, cssFullscreen]);

  useEffect(() => {
    const onFs = () => {
      const active = Boolean(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(active);
      if (!active) setChromeVisible(true);
    };
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const toggleFullscreen = async () => {
    const el = stageRef.current;
    if (!el) return;

    const supportsFs = el.requestFullscreen || el.webkitRequestFullscreen;
    if (!supportsFs) {
      setCssFullscreen((v) => !v);
      setChromeVisible(true);
      return;
    }

    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      } else if (el.requestFullscreen) {
        await el.requestFullscreen({ navigationUI: 'hide' });
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
      bumpChrome();
    } catch (_) {
      setCssFullscreen((v) => !v);
    }
  };

  const toTitleCase = (v) =>
    String(v || '')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const resolveAvatar = (profile) => {
    if (!profile) return '';
    const candidates = [
      profile?.avatar?.path,
      profile?.avatar?.url,
      profile?.avatar,
      profile?.userId?.avatar?.path,
      profile?.userId?.avatar?.url,
      profile?.userId?.avatar,
    ];
    return candidates.find((v) => typeof v === 'string' && v.trim()) || '';
  };

  const localName = toTitleCase(
    [
      currentProfile?.userId?.firstName || currentProfile?.firstName || '',
      currentProfile?.userId?.lastName || currentProfile?.lastName || '',
    ]
      .join(' ')
      .trim() || 'You',
  );
  const localAvatar = resolveAvatar(currentProfile);

  const CenteredState = ({ title, subtitle, actionLabel, onAction }) => (
    <div className="min-h-[calc(100vh-120px)] w-full flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center">
        <h2 className="text-2xl font-semibold text-nt-blue mb-3">{title}</h2>
        {subtitle && <p className="text-nt-grey mb-6 text-sm leading-relaxed">{subtitle}</p>}
        <div className="flex justify-center gap-3">
          <button
            type="button"
            className="bg-nt-blue text-white rounded-lg py-2.5 px-5 hover:opacity-90 transition inline-flex items-center gap-2"
            onClick={() => navigate(appointmentPath || '/courses')}
          >
            <FiArrowLeft size={16} />
            Back
          </button>
          {actionLabel && onAction && (
            <button
              type="button"
              className="bg-slate-100 text-nt-blue rounded-lg py-2.5 px-5 hover:bg-slate-200 transition"
              onClick={onAction}
            >
              {actionLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (!isCallActive) {
    return (
      <CenteredState
        title="No active call session"
        subtitle="Start the call from the appointment details page."
      />
    );
  }

  // Only block the stage on the *first* join — later rejoins keep the stage mounted
  // and show the Reconnecting banner instead of remounting the whole UI.
  if (!media || media.initialJoining) {
    return <CenteredState title="Preparing your call" subtitle="Connecting to the course room…" />;
  }

  const shellClass = cssFullscreen
    ? 'fixed inset-0 z-[9998] bg-slate-950'
    : 'relative rounded-2xl h-[calc(100vh-40px)] min-h-[560px] bg-slate-950 overflow-hidden';

  return (
    <div
      ref={stageRef}
      className={shellClass}
      onMouseMove={bumpChrome}
      onTouchStart={bumpChrome}
    >
      <div
        className={`absolute top-4 left-4 right-4 z-20 transition-opacity duration-300 ${
          chromeVisible || !(isFullscreen || cssFullscreen) ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="bg-slate-950/85 border border-white/20 rounded-full px-4 py-2.5 backdrop-blur-lg shadow-xl flex items-center justify-between">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 text-white text-sm px-3 py-1.5 transition-colors"
            onClick={() => {
              minimizeCall();
              navigate(appointmentPath || '/courses');
            }}
          >
            <FiArrowLeft size={14} />
            <span>Leave</span>
          </button>

          <div className="flex items-center gap-3">
            <CallStatsChip />
            {!timing.sessionStarted && timing.startInSeconds != null && (
              <span className="text-xs text-sky-200 tabular-nums bg-sky-500/20 rounded-full px-3 py-1">
                Starts in {formatCountdown(timing.startInSeconds)}
              </span>
            )}
            {closeInSeconds != null && (
              <span className="text-xs text-white/80 tabular-nums bg-white/10 rounded-full px-3 py-1">
                Ends in {formatCountdown(closeInSeconds)}
              </span>
            )}
            <button
              type="button"
              onClick={toggleFullscreen}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white"
              title={isFullscreen || cssFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen || cssFullscreen ? <FiMinimize2 size={14} /> : <FiMaximize size={14} />}
            </button>
          </div>
        </div>
      </div>

      <div className="h-full w-full relative">
        <CallStage
          localName={localName}
          localAvatar={localAvatar}
          chromeVisible={chromeVisible || !(isFullscreen || cssFullscreen)}
          onUserActivity={bumpChrome}
        />
      </div>
    </div>
  );
};

export { VideoChat };
